// ─────────────────────────────────────────────────────────────────────────────
// HowItWorksPage, the /how-it-works route.
//
// This is the highest-intent page on the public site, so it argues one thing
// only: you keep the register you already run, and Wavelength reconciles what
// comes out of it. The page is ordered the way an operator evaluates that
// claim. First the premise, then the four steps from connected source to
// exported journal, then what a single day actually feels like for the manager
// closing it and for the owner reading it the next morning, then the short list
// of things you need before you start, then how long the whole thing takes.
//
// It ends with the objections that actually stop a switch: disruption, staff
// retraining, and what happens to the data already sitting in the systems you
// run today.
//
// No photography or video exists yet, so every visual is a <MediaSlot>, a flat
// grey panel captioned with the asset that belongs in it. Nothing on the page
// names a customer, and nothing claims an integration that is not built.
// ─────────────────────────────────────────────────────────────────────────────
import { Link } from 'react-router-dom';
import {
    BTN_PRIMARY,
    BTN_SECONDARY,
    BTN_ON_DARK,
    BTN_ON_DARK_SOFT,
    Icon,
    Container,
    Section,
    Heading,
    CardTitle,
    Lead,
    Body,
    Key,
    Eyebrow,
    Pill,
    Badge,
    MediaSlot,
    SectionHead,
    FeatureRow,
    Steps,
    SpecList,
    StatBand,
    Accordion,
    CTASection,
    PageHero,
    MarketingShell,
    Seo,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

// The registers and systems an operator is likely to be running already. These
// are named as the things Wavelength sits beside, not as shipped connectors.
const COUNTER_SYSTEMS = ['Toast', 'Clover', 'Square', 'Lightspeed', 'Shopify', 'WooCommerce', 'QuickBooks'];

// ── The four steps, connect to journal ───────────────────────────────────────
const FLOW = [
    {
        title: 'Connect your sources',
        body: 'Point Wavelength at the systems that already hold your numbers: the registers in each store, the online store, the card processor and the bank statement you download anyway. Reading runs one way. Nothing is written back into your point of sale, and no one at the counter is asked to do anything differently.',
    },
    {
        title: 'Close the day where it happened',
        body: 'The manager who ran the shift counts the drawer blind, records the safe count and the deposit, and signs. A shortfall past the threshold you set will not submit without a written reason. On sign-off the day locks, and any later change needs approval and lands on the audit log.',
    },
    {
        title: 'Match register against processor against bank',
        body: 'Three numbers that should agree and usually do not: what the registers and the online store rang, what the card processor settled, and what the bank statement shows arriving. Wavelength lines all three up per location per day and hands you only the rows that failed to meet.',
    },
    {
        title: 'Export a journal your bookkeeper can defend',
        body: 'The closed period leaves as a QuickBooks journal CSV with tax kept on its own line. Your bookkeeper imports it, and any figure in it can be followed back through the deposit, the batch and the drawer to the orders that produced it.',
    },
];

// ── What one day looks like from both sides of the business ──────────────────
const CLOSING_SHIFT = {
    head: 'The manager closing up',
    note: 'Roughly twenty minutes at the end of trade, on an iPad at the safe.',
    rows: [
        { time: '8:40 pm', title: 'Trade stops', body: 'Register totals for the day are already in Wavelength. Nobody adds up a tape or writes a figure on a pad.' },
        { time: '8:50 pm', title: 'The drawer is counted blind', body: 'Denominations are keyed with the expected total hidden, so the count is a count rather than a confirmation.' },
        { time: '8:56 pm', title: 'A shortfall needs a sentence', body: 'The variance appears once the count is submitted. Past your threshold it will not go through without a written reason.' },
        { time: '9:02 pm', title: 'Safe count and deposit', body: 'What goes into the bag is recorded against the day it came from, with the person who bagged it on the record.' },
        { time: '9:08 pm', title: 'Sign off, day locked', body: 'The location is closed for that date. Reopening it is possible, and it is visible to everyone when it happens.' },
    ],
};

const NEXT_MORNING = {
    head: 'The owner reading it back',
    note: 'Roughly ten minutes with coffee, before anyone opens a store.',
    rows: [
        { time: '7:10 am', title: 'Every location on one screen', body: 'Each close from last night with its variance beside it. No group chat asking three managers how they finished.' },
        { time: '7:18 am', title: 'The exceptions queue', body: 'Only what failed to tie, each row carrying the location, the source, the date and the amount at stake.' },
        { time: '7:30 am', title: 'Card batches from last night', body: 'Processor settlement lined up against the batches the registers reported, so a missing payout is visible the next day rather than at month end.' },
        { time: '9:00 am', title: 'The bank statement goes in', body: 'The CSV from the bank is imported, duplicates are rejected on the way in, and deposits match themselves against sales and payouts.' },
        { time: 'Friday', title: 'The journal goes out', body: 'The closed week exports as a journal CSV. The bookkeeper works from a period that is already signed off.' },
    ],
};

// ── The short list of prerequisites ──────────────────────────────────────────
const PREREQS = [
    {
        title: 'A bank statement you can export',
        body: 'CSV, from the same screen you already use to download a PDF. One file per account. There is no bank feed to apply for and no credentials to hand over.',
    },
    {
        title: 'A register that can report the day',
        body: 'If your point of sale can produce end-of-day totals for a location, Wavelength can read the day. Sources beyond the ones we already read are added on request.',
    },
    {
        title: 'One location to begin with',
        body: 'Start with the store that gives you the most trouble. The rest come on once the first one has been closing cleanly for a week.',
    },
];

const HAVE_READY = [
    { label: 'Bank statement', value: 'CSV export, one file per account' },
    { label: 'Register reporting', value: 'End-of-day totals per location' },
    { label: 'Processor credentials', value: 'Datacap reporting key, if you use it' },
    { label: 'Locations and registers', value: 'A list, with the timezone each one trades in' },
    { label: 'People and approvals', value: 'Who counts, who signs, who approves' },
    { label: 'Accounts', value: 'The account names your bookkeeper already uses' },
];

const SCHEDULE = [
    { label: 'Connect the first location', value: 'An afternoon' },
    { label: 'First close, run alongside you', value: 'One evening' },
    { label: 'A month of bank statements imported and matched', value: 'Day two or three' },
    { label: 'Remaining locations switched on', value: 'The following week' },
    { label: 'Trading paused for any of it', value: 'Never' },
];

// ── Objections that actually stop a switch ───────────────────────────────────
const FAQ = [
    {
        q: 'Do I have to replace my point of sale?',
        a: 'No, and that is the point of the product. Wavelength is the back office, not the register. Your stores keep ringing sales on whatever they run today, and Wavelength reads the result. If you later change registers, the back office does not change with it, which is usually the more expensive half of a switch.',
    },
    {
        q: 'Does anything change for the people on the register?',
        a: 'Nothing at the counter. Cashiers ring sales exactly as they do now. The only new habit belongs to whoever closes the store: a blind drawer count, a safe count, a deposit and a signature, on an iPad, at the end of trade. Managers pick it up in one shift because it is the same ritual they already perform on paper.',
    },
    {
        q: 'What happens to the data already in the systems we run?',
        a: 'It stays exactly where it is. Wavelength reads from your sources and never writes back to them, so your point of sale, your online store and your accounting file are unchanged by connecting them. Everything Wavelength calculates lives in your own workspace on top of that data, and any correction is recorded as a new entry rather than an edit to the original.',
    },
    {
        q: 'Do we have to stop trading, or freeze a period, to start?',
        a: 'No. There is no cutover weekend and no closed books requirement. You connect one location, close that same evening, and carry on. History backfills behind you while the stores keep trading, so the first useful close happens before the migration is finished.',
    },
    {
        q: 'How much history comes across?',
        a: 'Order history comes across with the connection, so trends and customer records start populated rather than empty. Bank activity goes back as far as your bank will export statements, which for most accounts is far enough to reconcile the last several months if you want the comfort of doing it.',
    },
    {
        q: 'What if we decide to leave?',
        a: 'Every table in the product exports to CSV, Excel or a branded PDF from the page you are looking at, including the closes, the reconciliation and the audit log. Your source systems were never modified, so leaving means turning off a login rather than unpicking a migration.',
    },
];

// ── A single column of the hour-by-hour panel ────────────────────────────────
function DayColumn({ column, className = '' }) {
    return (
        <div className={`flex flex-col ${className}`}>
            <div className="border-b border-[rgba(10,10,10,0.08)] px-6 py-6 sm:px-8">
                <CardTitle>{column.head}</CardTitle>
                <Body className="mt-1.5">{column.note}</Body>
            </div>
            {column.rows.map((r, i) => (
                <div
                    key={r.title}
                    className={`flex gap-5 px-6 py-5 sm:px-8 ${i > 0 ? 'border-t border-[rgba(10,10,10,0.06)]' : ''}`}
                >
                    <span className="w-[58px] flex-none pt-[2px] text-[13px] font-medium tabular-nums text-[#A1A1A1]">{r.time}</span>
                    <div>
                        <div className="text-[15px] font-medium tracking-[-0.01em] text-[#0A0A0A]">{r.title}</div>
                        <Body className="mt-1">{r.body}</Body>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function HowItWorksPage() {
    return (
        <MarketingShell>
            <Seo
                title="How it works: back office for your existing POS"
                description="Back office software that works with your existing POS. Connect your registers, processor and bank CSV, close each day, reconcile all three, export a journal."
                path="/how-it-works"
                keywords="back office software that works with my existing POS, POS agnostic reporting, keep your POS back office, multi location daily close process, three way reconciliation register processor bank, bank statement CSV import reconciliation, QuickBooks journal entry export retail, back office without replacing point of sale"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'WebPage',
                            '@id': 'https://wavelengtherp.com/how-it-works#webpage',
                            url: 'https://wavelengtherp.com/how-it-works',
                            name: 'How Wavelength works with the point of sale you already run',
                            description: 'Connect your registers, online store, card processor and bank statement, close each day per location, reconcile all three sources, and export a journal for your bookkeeper.',
                            publisher: { '@id': 'https://wavelengtherp.com/#organization' },
                        },
                        {
                            '@type': 'BreadcrumbList',
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://wavelengtherp.com/' },
                                { '@type': 'ListItem', position: 2, name: 'How it works', item: 'https://wavelengtherp.com/how-it-works' },
                            ],
                        },
                        {
                            '@type': 'FAQPage',
                            mainEntity: FAQ.map((f) => ({
                                '@type': 'Question',
                                name: f.q,
                                acceptedAnswer: { '@type': 'Answer', text: f.a },
                            })),
                        },
                    ],
                }}
            />

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <PageHero
                eyebrow="How it works"
                title="Keep your register. We reconcile it."
                lead={<>Wavelength is a <Key>back office that works with the point of sale you already have</Key>. It reads what your stores and your online orders produced, closes each day where it happened, and proves the money reached the bank.</>}
                primary={{ label: 'Start free', to: '/signup' }}
                secondary={{ label: 'Book a walkthrough', to: '/contact' }}
            >
                <Reveal>
                    <MediaSlot
                        kind="Screenshot"
                        label="The reconciliation screen with register, processor and bank side by side"
                        note="Laptop, straight on, three columns matching and two rows left over"
                        ratio="16 / 9"
                        className="!rounded-[32px]"
                    />
                </Reveal>
            </PageHero>

            {/* ── The premise ────────────────────────────────────────────── */}
            <Section id="premise" pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="The premise"
                            title="Your register tells you what you rang."
                            body="It does not tell you whether the bank received it. That gap is where the evening goes, and no amount of reporting inside the register closes it, because the register can only ever see its own half of the transaction."
                            bullets={[
                                'Stores keep ringing sales on whatever they run today',
                                'Wavelength reads from your systems and never writes back to them',
                                'Every location on one screen, whatever sits on each counter',
                                'One day, one close, one number, per location',
                            ]}
                            cta={<Link to="/integrations" className={BTN_SECONDARY}>What it reads<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Photo"
                                    label="A counter after close with the till drawer open and an iPad beside it"
                                    note="Low light, shutters down, the screen is the brightest thing in the frame"
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                    <Reveal delay={120}>
                        <div className="mt-14 flex flex-col items-center gap-5 text-center">
                            <span className="text-[13px] text-[#8A8A8A]">Works alongside whatever runs the counter</span>
                            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
                                {COUNTER_SYSTEMS.map((l) => (
                                    <span key={l} className="text-[17px] font-medium tracking-[-0.01em] text-[#C4C4C4]">{l}</span>
                                ))}
                            </div>
                            <p className="max-w-[560px] text-[13px] leading-[1.6] text-[#A1A1A1]">
                                Card settlement comes through Datacap. Bank activity arrives as a CSV statement you export yourself. Sources beyond the ones we read today are added on request.
                            </p>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── The four steps ─────────────────────────────────────────── */}
            <Section id="steps" tone="paper2" pad="lg">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[380px_1fr] lg:gap-16">
                        <Reveal>
                            <div className="flex flex-col gap-4 lg:sticky lg:top-28">
                                <Eyebrow>The flow</Eyebrow>
                                <Heading>Four steps, connect to journal.</Heading>
                                <Lead>Every one of them happens on top of the systems you already pay for. None of them asks a cashier to learn a new screen.</Lead>
                                <div className="mt-2">
                                    <Link to="/features" className={BTN_PRIMARY}>Everything in the platform<Icon name="arrow" className="h-4 w-4" /></Link>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <Steps items={FLOW} />
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── Dark panel: one dashboard, whatever each store runs ─────── */}
            <Section pad="sm">
                <Container>
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[28px] bg-[#111111] p-6 text-white sm:p-12 lg:p-14">
                            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_340px] lg:gap-14">
                                <div className="flex flex-col">
                                    <Eyebrow className="!text-white/45">Whatever each store runs</Eyebrow>
                                    <h2 className="mt-4 max-w-[440px] text-[28px] font-semibold leading-[1.08] tracking-[-0.025em] text-white sm:text-[36px]">
                                        One dashboard for stores that do not match.
                                    </h2>
                                    <p className="mt-5 max-w-[440px] text-[15px] leading-[1.65] text-white/60">
                                        Acquired locations rarely run the same register, and replacing them all is a project nobody wants. Wavelength sits above the differences, so the day, the tenders and the rules are the same everywhere even when the hardware is not.
                                    </p>
                                    <div className="mt-8 flex flex-wrap gap-2">
                                        {['Registers in store', 'Online orders', 'Card processor', 'Bank statement', 'Scales', 'Receiving', 'Delivery'].map((c, i) => (
                                            <Pill key={c} dark active={i === 0}>{c}</Pill>
                                        ))}
                                    </div>
                                    <div className="mt-auto pt-12">
                                        <h3 className="text-[19px] font-semibold tracking-[-0.015em] text-white sm:text-[22px]">
                                            Reading only, in one direction
                                        </h3>
                                        <p className="mt-2.5 max-w-[460px] text-[14px] leading-[1.65] text-white/55">
                                            Nothing is written back into the systems you connect. If Wavelength were switched off tomorrow, your register, your online store and your accounting file would be exactly as you left them.
                                        </p>
                                        <div className="mt-7 flex flex-wrap gap-2">
                                            <Link to="/integrations" className={BTN_ON_DARK}>See what it reads</Link>
                                            <Link to="/security" className={BTN_ON_DARK_SOFT}>How it is built</Link>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-center">
                                    <MediaSlot
                                        dark
                                        kind="Diagram"
                                        label="Sources flowing into one workspace"
                                        note="Registers, online orders, processor and bank statement on the left, one reconciled day on the right"
                                        ratio="3 / 4"
                                        className="!rounded-[28px] max-w-[340px]"
                                    />
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── A day, hour by hour ────────────────────────────────────── */}
            <Section id="day" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="A day in practice"
                            title="What the day actually looks like."
                            lead="Two people, two moments. The manager who ran the shift closes it while the details are still fresh, and the owner reads a settled day the next morning instead of chasing one."
                            maxWidth="max-w-[560px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <div className="mt-12 grid grid-cols-1 overflow-hidden rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white lg:grid-cols-2">
                            <DayColumn column={CLOSING_SHIFT} />
                            <DayColumn column={NEXT_MORNING} className="border-t border-[rgba(10,10,10,0.08)] lg:border-l lg:border-t-0" />
                        </div>
                    </Reveal>
                    <Reveal delay={120}>
                        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr]">
                            <MediaSlot
                                kind="Screenshot"
                                label="Blind drawer count mid entry on an iPad"
                                note="Denominations being keyed at the safe, expected total not on screen"
                                ratio="4 / 3"
                                className="!rounded-[28px]"
                            />
                            <MediaSlot
                                kind="Screenshot"
                                label="The exceptions queue the next morning"
                                note="Laptop, a short list with location, source, date and amount"
                                ratio="4 / 3"
                                className="!rounded-[28px]"
                            />
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── What you need before you start ─────────────────────────── */}
            <Section id="before" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="What you need before you start."
                            lead="Shorter than you expect, because the parts that usually hold a project up are the parts Wavelength deliberately does not require."
                            maxWidth="max-w-[520px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <div className="mt-12 grid grid-cols-1 overflow-hidden rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white sm:grid-cols-3">
                            {PREREQS.map((p, i) => (
                                <div key={p.title} className={`flex flex-col gap-3 p-7 ${i > 0 ? 'border-t border-[rgba(10,10,10,0.08)] sm:border-l sm:border-t-0' : ''}`}>
                                    <span className="text-[13px] font-medium tabular-nums text-[#A3A3A3]">{String(i + 1).padStart(2, '0')}</span>
                                    <div className="text-[17px] font-semibold tracking-[-0.015em] text-[#0A0A0A]">{p.title}</div>
                                    <Body>{p.body}</Body>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={120}>
                        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[340px_1fr] lg:gap-14">
                            <div className="flex flex-col gap-3">
                                <CardTitle>Have these to hand</CardTitle>
                                <Body>Nothing here needs a project plan. If you can find all six in an afternoon, you can be closing a real day the same evening.</Body>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    <Badge>No bank credentials</Badge>
                                    <Badge>No data migration</Badge>
                                    <Badge>No trading freeze</Badge>
                                </div>
                            </div>
                            <SpecList rows={HAVE_READY} />
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── How long it takes ──────────────────────────────────────── */}
            <Section id="timeline" tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="Timing"
                            title="Live in a week, not two quarters."
                            lead="There is no cutover weekend, because there is nothing to cut over from. You add a layer above what you run, and the first closed day arrives before the rollout is finished."
                            actions={<Link to="/pricing" className={BTN_SECONDARY}>See pricing<Icon name="arrow" className="h-4 w-4" /></Link>}
                            maxWidth="max-w-[560px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <StatBand
                            className="mt-12"
                            countUp
                            cols={3}
                            stats={[
                                { value: '1', label: 'Location to start with', note: 'Add the rest once the first one is boring' },
                                { value: '4', label: 'Steps from connect to journal', note: 'Connect, close, reconcile, export' },
                                { value: '0', label: 'Changes at the register', note: 'Staff ring sales the way they already do' },
                            ]}
                        />
                    </Reveal>
                    <Reveal delay={120}>
                        <div className="mt-4">
                            <SpecList rows={SCHEDULE} />
                        </div>
                    </Reveal>
                </Container>
            </Section>

            {/* ── FAQ ────────────────────────────────────────────────────── */}
            <Section id="faq" pad="lg">
                <Container>
                    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[340px_1fr] lg:gap-16">
                        <Reveal>
                            <div className="flex flex-col gap-4 lg:sticky lg:top-28">
                                <Heading>Switching, honestly.</Heading>
                                <Lead>The questions that decide whether a back office actually gets adopted, answered without hedging.</Lead>
                                <div className="mt-2">
                                    <Link to="/faq" className={BTN_SECONDARY}>All questions<Icon name="arrow" className="h-4 w-4" /></Link>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <Accordion items={FAQ} />
                        </Reveal>
                    </div>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Get started"
                title="Bring one location and one bank statement."
                body="We connect a store, close a day with you and import a month of bank activity. By the end of it you are reading a reconciled day instead of assembling one."
                primary={{ label: 'Start free', to: '/signup' }}
                secondary={{ label: 'Book a walkthrough', to: '/contact' }}
            />
        </MarketingShell>
    );
}
